/**
 * As-of: what the library looked like at a past moment, and the `MOVE` column that follows.
 *
 * `rating_event` is append-only, so the rating of every song at any past timestamp is the latest
 * event for that song with `created_at <= T`. Ranks as-of `T` follow from that with the ordinary
 * ranking arithmetic. No snapshots, no rank history table, no recalculation cadence — ruled in
 * docs/DESIGN-CONFLICTS.md §I3, which also settles the harder half:
 *
 * **The "since when" problem dissolves rather than being answered.** The system stops guessing a
 * baseline; the user picks one. That is why the baselines below are a closed vocabulary the client
 * and server share rather than a date the client invents and the server trusts.
 */
export const BASELINES = [
    { kind: "last-session", label: "LAST SESSION" },
    { kind: "7-days", label: "7 DAYS" },
    { kind: "30-days", label: "30 DAYS" },
    { kind: "90-days", label: "90 DAYS" },
    { kind: "year-start", label: "THIS YEAR" },
];
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Resolves a baseline to the instant the comparison runs from.
 *
 * `lastSessionStart` is supplied rather than derived here because it is a fact about the event log
 * — the server computes it — and a client guessing at it would produce a `MOVE` column that
 * disagrees with the one the server would produce for the same choice.
 *
 * Returns null when the baseline cannot be resolved: a library with no rating history has no last
 * session, and the honest answer is no comparison rather than a comparison against now.
 */
export function baselineAt(kind, now, lastSessionStart) {
    switch (kind) {
        case "last-session":
            return lastSessionStart;
        case "7-days":
            return new Date(now.getTime() - 7 * DAY_MS);
        case "30-days":
            return new Date(now.getTime() - 30 * DAY_MS);
        case "90-days":
            return new Date(now.getTime() - 90 * DAY_MS);
        case "year-start":
            return new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
        case "custom":
            return null;
    }
}
/**
 * A session boundary in the rating log.
 *
 * "Last session" has to mean something, and the log is the only witness. A gap of six hours starts
 * a new one: long enough that a sitting interrupted by lunch stays one session, short enough that
 * yesterday evening and this morning are two. Sittings are the unit this app is built around —
 * the booth exists to make them happen — so the baseline that matters most is "since I last sat
 * down with this".
 */
export const SESSION_GAP_MS = 6 * 60 * 60 * 1000;
/**
 * `MOVE = rank(as-of T) − rank(now)`, so a positive number is places GAINED.
 *
 * Note the direction: ranks count down toward 1, so the subtraction is the other way round from
 * the naive reading. Getting this backwards renders every rise as a fall, and it would look
 * plausible on a screenshot — which is why it lives here with a test rather than in a template.
 *
 * `new` is a first rating inside the window, not merely the absence of a previous rank: a song
 * rated in 2019, cleared, and rated again today is not new, and calling it new would overstate
 * what changed. §I3 defines it on the first event for exactly that reason.
 */
export function move(rank, previousRank, isNew) {
    if (isNew)
        return { kind: "new", places: null };
    if (rank === null || previousRank === null)
        return { kind: "none", places: null };
    const places = previousRank - rank;
    if (places === 0)
        return { kind: "flat", places: 0 };
    return { kind: places > 0 ? "up" : "down", places };
}
/** `+12`, `−3`, `NEW`, or an em dash. Never `0` with a sign, and never a bare minus-hyphen. */
export function moveLabel(m) {
    switch (m.kind) {
        case "new":
            return "NEW";
        case "flat":
            return "·";
        case "none":
            return "—";
        default:
            return m.places > 0 ? `+${m.places}` : `−${Math.abs(m.places)}`;
    }
}
//# sourceMappingURL=asof.js.map